// Wire protocol between the two devices (all JSON over Trystero data channels,
// except image blobs which use binary actions with JSON metadata).

import type { FaceBox, PoseAdjust } from '../booth/layout';
import type { PoseFrameMsg } from '../booth/pose';
import type { Decision, LayoutState } from '../booth/solver';
import type { BackgroundId, FilterId, FrameColorId, LayoutId } from '../booth/themes';

export type { PoseFrameMsg, Decision, LayoutState };

export type Role = 'host' | 'guest';

/** How a sender separates itself from its background. */
export type KeyMode = 'ai' | 'greenscreen';

export interface Hello {
  role: Role;
  name: string;
  v: number;
  /** v3+: capabilities, e.g. 'barcode' (seq strip in the packed frame), 'pf' (per-frame pose messages) */
  caps?: string[];
}

/** Shared booth settings. Host-authoritative. */
export interface BoothConfig {
  background: BackgroundId;
  /** with background 'member-camera': whose camera shows the scene */
  sceneSlot: number;
  customBgRev: number;
  layout: LayoutId;
  frameColor: FrameColorId;
  filter: FilterId;
  countdown: number; // seconds per shot
  caption: string;
  stickers: boolean;
  /** saved photo: people area as seen in the shared mirror (true, default) or camera view (false) */
  mirrorPhoto?: boolean;
}

export const DEFAULT_CONFIG: BoothConfig = {
  /** v0.8: the room owner's own camera is the scene (they are not cut out); others join it */
  background: 'member-camera',
  sceneSlot: 0,
  customBgRev: 0,
  layout: 'strip4',
  frameColor: 'white',
  filter: 'kbeauty',
  countdown: 5,
  caption: 'together ♡',
  stickers: true,
};

/**
 * ~15 Hz: where the sender's face is and how they moved/zoomed themselves.
 * v0.5: body keypoints / eyes / colour travel per frame in PoseFrameMsg ('pf'),
 * joined to the decoded video frame by the barcode seq (booth/pose.ts).
 */
export interface PoseMsg {
  face: FaceBox;
  /** false = rear camera (not mirrored) */
  mirrored: boolean;
  noFace: boolean;
  adjust: PoseAdjust;
  mode: KeyMode;
  fps: number;
}

export interface TimePing { t0: number }
export interface TimePong { t0: number; t1: number; t2: number }

/** Host → guest: fire shot `index` at host-clock time `atHost`. */
export interface ShotCmd {
  session: string;
  index: number;
  total: number;
  atHost: number;
  countdownMs: number;
}

/** Guest → host: shot executed; lateMs = local timer error, firedHost = guest's fire time in host clock. */
export interface ShotAck {
  session: string;
  index: number;
  firedHost: number;
  lateMs: number;
  /** v0.7.2: shutter candidates on the host clock (metadata only) */
  cands?: { k: number; tHost: number; motion: number }[];
}

/** Host → guest: the agreed pair of shutter candidates for one shot. */
export interface BurstPick { session: string; index: number; guest: number | null }

/** Host → everyone at the start of a photo session. */
export interface SessionInfo {
  session: string;
  /** 'server' when the session is stored on the Booth API; null = previews only */
  key: string | null;
  total: number;
  config: BoothConfig;
  /** slots taking part (host = 0) */
  slots: number[];
  startedAt: number;
}

/** Small cut-out shared with everyone right after each shot (live room gallery). */
export interface PreviewMeta {
  session: string;
  index: number;
  slot: number;
  face: FaceBox;
  adjust: PoseAdjust;
  masked: boolean;
  mirrored: boolean;
  noFace: boolean;
  /** v0.5: sender's peer id (keys of the decision) */
  peerId?: string;
  /** v0.5: pose of the captured frame (keypoints + eyes + colour stats), un-mirrored */
  pose?: PoseFrameMsg;
  /** v0.5: layer/contact decision frozen at the shutter instant (US7) */
  decision?: Decision;
  /** v0.5: saved-photo mirror setting at the shutter instant */
  mirrorPhoto?: boolean;
}

/** Guest → host: "use my surroundings as the backdrop". */
export interface SceneRequest {
  slot: number;
  name: string;
}

/** Host → everyone: the session is stored, these photos can be downloaded. */
export interface DoneMsg {
  session: string;
  photos: { id: string; sessionId: string; kind: 'strip' | 'shot'; index: number }[];
}

export const PROTOCOL_VERSION = 3;
/** capabilities this build announces in Hello.caps */
export const CAPS = ['barcode', 'pf', 'layout'];
